export type AnalyticsConsent = boolean | null

export const ANALYTICS_CONSENT_COOKIE = 'spatius_analytics_consent'

const LEGACY_STORAGE_KEYS = ['cookie-consent', 'spatius-cookie-consent']
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365
const SHARED_COOKIE_DOMAINS = ['spatius.ai', 'spatialwalk.cn']

function parseConsent(value: string | null | undefined): AnalyticsConsent {
  if (value === 'granted' || value === 'accepted' || value === 'true')
    return true
  if (value === 'denied' || value === 'declined' || value === 'false')
    return false
  return null
}

function getSharedCookieDomain(): string | undefined {
  if (typeof window === 'undefined')
    return undefined

  const hostname = window.location.hostname.toLowerCase()
  const sharedDomain = SHARED_COOKIE_DOMAINS.find(domain => hostname === domain || hostname.endsWith(`.${domain}`))
  return sharedDomain ? `.${sharedDomain}` : undefined
}

function writeConsentCookie(consent: AnalyticsConsent): void {
  if (typeof document === 'undefined' || typeof window === 'undefined')
    return

  const domain = getSharedCookieDomain()
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  const domainPart = domain ? `; Domain=${domain}` : ''
  const maxAge = consent === null ? 0 : COOKIE_MAX_AGE_SECONDS
  const value = consent === true ? 'granted' : consent === false ? 'denied' : ''
  const name = encodeURIComponent(ANALYTICS_CONSENT_COOKIE)

  // Remove the old host-only variant before writing the shared cookie. This is
  // the only cookie migration analytics consent needs.
  if (domain)
    document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}`
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${domainPart}${secure}`
}

function clearLegacyConsent(): void {
  if (typeof window === 'undefined')
    return
  try {
    for (const key of LEGACY_STORAGE_KEYS)
      window.localStorage.removeItem(key)
  }
  catch {
    // The shared cookie remains the source of truth when storage is blocked.
  }
}

function readConsentCookie(): AnalyticsConsent | undefined {
  if (typeof document === 'undefined')
    return undefined

  const prefix = `${encodeURIComponent(ANALYTICS_CONSENT_COOKIE)}=`
  const values: string[] = []
  for (const part of document.cookie.split(';')) {
    const cookie = part.trim()
    if (!cookie.startsWith(prefix))
      continue
    try {
      values.push(decodeURIComponent(cookie.slice(prefix.length)))
    }
    catch {
      writeConsentCookie(null)
      return null
    }
  }

  if (!values.length)
    return undefined
  if (new Set(values).size !== 1) {
    writeConsentCookie(null)
    return null
  }
  return parseConsent(values[0])
}

export function readAnalyticsConsent(): AnalyticsConsent {
  const cookieConsent = readConsentCookie()
  if (cookieConsent !== undefined) {
    clearLegacyConsent()
    return cookieConsent
  }

  if (typeof window === 'undefined')
    return null

  try {
    for (const key of LEGACY_STORAGE_KEYS) {
      const legacyConsent = parseConsent(window.localStorage.getItem(key))
      if (legacyConsent === null)
        continue
      writeConsentCookie(legacyConsent)
      clearLegacyConsent()
      return legacyConsent
    }
  }
  catch {
    // Storage can be unavailable in privacy modes.
  }
  return null
}

export function writeAnalyticsConsent(consent: AnalyticsConsent): void {
  clearLegacyConsent()
  writeConsentCookie(consent)
}

/** Expire a cookie in both its host-only and shared-domain variants. */
export function expireSharedCookie(key: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined')
    return

  const domain = getSharedCookieDomain()
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  const name = encodeURIComponent(key)
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}`
  if (domain)
    document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax; Domain=${domain}${secure}`
}

/** Remove the active Project's persistence before shutdown or a trusted identity bootstrap. */
export function clearPostHogPersistence(token: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined')
    return

  const domain = getSharedCookieDomain()
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  const normalizedToken = token.trim()
  if (!/^[\w-]{1,200}$/.test(normalizedToken))
    return
  const keys = [`ph_${normalizedToken}_posthog`, `__ph_opt_in_out_${normalizedToken}`]
  for (const key of keys) {
    const name = encodeURIComponent(key)
    document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}`
    if (domain)
      document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax; Domain=${domain}${secure}`
    try {
      window.localStorage.removeItem(key)
      window.sessionStorage.removeItem(key)
    }
    catch {
      // Cookie cleanup still applies when storage is blocked.
    }
  }
}
